import axios from "axios";
import { API_URL } from "../constant";

/**
 * The backend allowlists request origins by exact match (this is what replaced CSRF
 * tokens). A 403 here means we're calling the wrong backend domain, or the dev proxy isn't
 * rewriting Origin — not a user-facing problem, and silently swallowing it costs hours.
 *
 * Shared with sessionAxios, which needs the same diagnostic but none of the 401 handling.
 */
export const warnIfOriginBlocked = (error: any) => {
  if (
    error.response?.status === 403 &&
    error.response?.data?.message === "Request origin not allowed"
  ) {
    console.error(
      `[auth] Origin ${window.location.origin} is not on the backend allowlist. ` +
        `In dev, check src/setupProxy.js rewrites Origin; in prod, check the app is ` +
        `calling the backend on its own domain.`
    );
  }
};

/**
 * The HTTP client for application requests. Everything that talks to our API goes through
 * it — except the session bootstrap and logout, which use `sessionAxios` (see below).
 *
 * The session is an httpOnly cookie set by the backend, so JS can neither read it nor set
 * an Authorization header from it. `withCredentials` is what makes the browser attach it —
 * without it the cookie is neither stored on login nor sent afterwards.
 *
 * Do NOT use this for S3 signed-URL uploads: those are pre-authorised and must not receive
 * credentials. Use bare `axios` there (see UploadCardImage / Sliders / PopUps).
 */
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Add a response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // The session is gone or was revoked. We can't clear an httpOnly cookie ourselves —
    // the backend already has, or it was never valid. Just get out of the authed UI.
    //
    // The /login guard matters: this is a full page reload, so a 401 raised *on* the login
    // page would reload into another 401 and loop forever.
    if (
      error.response?.status === 401 &&
      window.location.pathname !== "/login"
    ) {
      window.location.href = "/login";
    }

    warnIfOriginBlocked(error);

    return Promise.reject(error);
  }
);

export default axiosInstance;
